<template>
  <v-container fluid justify-start>

    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="profiles"
      :loading="isLoading"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      item-key="id"
      :loading-text="getExpression('Alert', 'LoadingItems')"
      :footer-props="{
        'items-per-page-text': getExpression('Label', 'RowsPerPage')
      }"
      class="elevation-2"
      >
      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate></v-progress-linear>

      <template v-slot:top>

        <!-- Header Toolbar -->
        <v-toolbar dense dark flat :color="toolbarColor">

          <app-back-button v-bind:color="toolbarColorDark" />

          <!--<v-icon large :color="appColor_Abby" class="mr-2">library_books</v-icon>-->
          <v-toolbar-title :class="titleClass">{{ title }}</v-toolbar-title>
          <v-spacer></v-spacer>

          <!-- Edit Dialog -->
          <v-toolbar-items>
            <v-dialog v-model="editDialog" max-width="350px" :persistent="true">
              <loading :active.sync="isSaving" :can-cancel="false" loader="dots" :color="appColor_Progress"></loading>
              <template v-slot:activator="{ on }">
                <v-btn v-if="isSmallDevice" small dark slot="activator" :color="toolbarColorDark" class="mb-1 mt-0 mr-0 elevation-0" :disabled="disableAdd" v-on="on">New</v-btn>
                <v-btn v-else slot="activator" :color="toolbarColorDark" small dark class="mb-1 mt-0 mr-0 elevation-0" :disabled="disableAdd" v-on="on">{{ getExpression('Button', 'CreatePlaylist') }}</v-btn>
              </template>
              <v-toolbar dense dark flat :color="editToolbarColorDark">
                <v-toolbar-title><v-icon class="pr-2">edit</v-icon> {{ formTitle }}</v-toolbar-title>
              </v-toolbar>
              <v-card>
                <v-card-text class="pt-3 pl-2 pr-2 pb-0 ma-0">
                  <v-container grid-list-md class="pt-0 pb-0">
                    <form>
                      <v-row no-gutters>
                        <v-col cols=12 xs="12">
                          <v-text-field
                            v-model="editedProfile.name"
                            :label="getExpression('Label', 'Name')"
                            v-validate="'required|max:50'"
                            :error-messages="errors.collect('name')"
                            data-vv-name="name"
                            maxlength="50"
                            ref="name"
                          />
                        </v-col>
                        <v-col cols=12 xs="12">
                          <v-text-field
                            v-model="editedProfile.description"
                            :label="getExpression('Label', 'Description')"
                            maxlength="100"
                          />
                        </v-col>
                        <v-col v-if="editedProfile.id > 0" cols=12 xs="6">
                          <v-switch
                            v-model="editedProfile.isActive"
                            :disabled="editedProfile.isDisableActivate"
                            :label="getExpression('Label', 'Activate')"
                            class="mt-1 pa-0"
                          />
                        </v-col>
                      </v-row>
                    </form>
                  </v-container>
                </v-card-text>
                <v-card-actions class="pt-0">

                  <v-spacer />

                  <v-btn @click="closeDialog" :loading="isSaving" class="rounded-0" large>{{ getExpression('Button', 'Cancel') }}</v-btn>
                  <v-btn :color="toolbarColorDark" dark @click="save" :loading="isSaving" class="rounded-0 ml-4" large>{{ getExpression('Button', 'Save') }}</v-btn>

                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Filters -->
        <v-row no-gutters class="mx-3 mt-2">

          <!-- Kiosks Dropdown -->
          <v-col v-if="showKiosk" cols="12" sm="4">
            <kiosk-combobox
              v-model="kioskId"
              :options="kiosks"
              :label="getExpression('Label', 'Kiosk')"
              :placeholder="getExpression('Label', 'SelectKiosk')"
              :disabled="disableKiosk"
              class="mr-3"
            />
          </v-col>

          <!-- Residents Dropdown -->
          <v-col v-if="showResident" cols="12" sm="4">
            <v-select
              v-model="residentId"
              item-value="id"
              item-text="name"
              :items="filteredResidents"
              :label="getExpression('Label', 'Resident')"
              placeholder="Select Resident"
              @change="loadProfiles()"
              :disabled="disableResident"
              class="mr-3"
            />
          </v-col>

          <!-- Search Text -->
          <v-col cols="12" :sm="searchColWidth" >
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="getExpression('Label', 'Search')"
              :disabled="disableSearch"
              clearable
            />
          </v-col>
        </v-row>
      </template>
      
      <template v-slot:item.name="{ item }">
        <span class="text-left">
          <span @click="edit(item)" style="white-space: nowrap">
            <span :style="appStyle_HyperLink">{{ item.name }}</span>
            <v-icon small class="ml-2" style="cursor: pointer">edit</v-icon>
          </span>
        </span>
      </template>

      <template v-slot:item.items="{ item }">
        <v-btn small dark @click="editMedia(item)" :color="toolbarColorDark" height="36" class="rounded-0">{{ getExpression('Button', 'EditContent') }}</v-btn>
      </template>

      <template v-slot:item.isActive="{ item }">
        <span class="mr-4">
          <v-icon v-if="item.isActive" :color="appColor_Selected">done</v-icon>
        </span>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span class="text-left pl-0 pr-0"><app-date :date="item.createDate" :format="'MMM-DD-YYYY'" /></span>
      </template>

      <template v-slot:item.action="{ item }">
        <v-icon v-if="!item.isActive" @click="remove(item)">delete</v-icon>
        <v-icon v-else color="grey lighten-2">delete</v-icon>
      </template>
    </v-data-table>

    <!-- Profile Detail -->
    <v-dialog v-model="detailsDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <abby-profile-detail
        v-bind:id="editedProfile.id"
        v-bind:sourceId="sourceId_Resident"
        v-bind:mediaPathTypes="mediaPathTypes"
        v-bind:profileDetails="profileDetails"
        v-bind:name="editedProfile.name"
        v-bind:ownerName="residentName"
        v-bind:subscriptionLevel="subscriptionLevel"
        v-bind:defaultContainer="defaultContainer"
        v-bind:isActive="activeProfileId === editedProfile.id"
        v-bind:toolbarColor="toolbarColor"
        v-bind:toolbarColorDark="toolbarColorDark"
        v-on:cancel="detailCancel"
        v-on:done="detailDone"
        >
      </abby-profile-detail>
    </v-dialog>

  </v-container>
</template>

<script>
import appService from '@/app.service'
import AbbyProfileDetail from '@/components/AbbyProfileDetail'
import { mapGetters } from 'vuex'
import { AppColor, AppStyle, Base64Image, Container, AbbyMediaSourceId, UserRole, KioskTypeId, MediaPathTypeId, ImageUri } from '@/config.js'
import KioskCombobox from '@/components/KioskCombobox'
import AppDate from '@/components/App/AppDate'
import AppBackButton from '@/components/App/AppBackButton'

export default {
  name: 'AbbyResidentProfileManager',
  components: {
    AbbyProfileDetail,
    KioskCombobox,
    AppDate,
    AppBackButton
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {
      // colors
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,
      appColor_Abby: AppColor.KioskTypeAbbyLight,
      appColor_AbbyDark: AppColor.KioskTypeAbby,
      appColor_Resident: AppColor.ResidentLight,
      appColor_ResidentDark: AppColor.ResidentDark,

      // styles
      appStyle_HyperLink: AppStyle.HyperLink,

      // containers
      residentPrefix: Container.ResidentPrefix,
      publicContainer: Container.Public,

      // image uri
      //base64Image_ProcessingIcon: Base64Image.ProcessingIcon,
      base64Image_AudioIcon: Base64Image.AudioIcon,
      imageUri_AudioVideoPlaceholder: ImageUri.AudioVideoPlaceHolder,

      // source ids
      sourceId_Resident: AbbyMediaSourceId.Resident,

      // roles
      role_Admin: UserRole.Admin,
      role_Manager: UserRole.Manager,

      // kiosk types
      kioskTypeId_Genie: KioskTypeId.Genie,
      kioskTypeId_Abby: KioskTypeId.Abby,

      // path types
      pathTypeId_Ambient: MediaPathTypeId.VideosAmbient,
      pathTypeId_AudioRadioShows: MediaPathTypeId.AudioRadioShows,
      pathTypeId_ImagesPersonal: MediaPathTypeId.ImagesPersonal,
      pathTypeId_VideosHomeMovies: MediaPathTypeId.VideosHomeMovies,
      pathTypeId_VideosPetCat: MediaPathTypeId.VideosPetCat,
      pathTypeId_VideosTurnWheel: MediaPathTypeId.VideosTurnWheel,

      isLoading: false,
      isSaving: false,
      isDeleting: false,
      search: '',
      editDialog: false,
      detailsDialog: false,
      residents: [],
      residentId: null,
      kiosks: [],
      kioskId: -1,
      profiles: [],
      profileDetails: [],
      mediaPathTypes: [],
      
      editedIndex: -1,
      editedProfile: {
        id: -1,
        residentId: null,
        kioskId: null,
        name: null,
        description: null,
        isActive: null,
        isDisableActivate: false,
        createDate: null
      },
      defaultProfile: {
        id: -1,
        residentId: null,
        kioskId: null,
        name: null,
        description: null,
        isActive: null,
        isDisableActivate: false,
        createDate: null
      }
    }
  },
  computed: {
    ...mapGetters([
      'profile',
      'activeResidentItem',
      'activeKioskItem',
      'activeResidentKioskItem',
      'isAdmin',
      'isKioskManager',
      'isResidentManager',
      'previousRoute',
      'locale']),
    title () {
      if (this.isAdmin) return 'Resident Playlists'
      return this.isFromKioskDashboard
          ? this.kioskName
          : `${this.residentKioskName} / ${this.residentName}`
    },
    formTitle () {
      return this.editedProfile.id < 0
        ? this.getExpression('FormTitle', 'NewPlaylist')
        : this.getExpression('FormTitle', 'EditPlaylist')
    },
    noDataText () {
      if (this.isLoading) return this.getExpression('Alert', 'OneMoment')

      if (this.residents.length === 0) {
        return this.getExpression('Alert', 'NoAbbyResidents')
      }

      if (this.profiles.length === 0 && this.residentId !== null) {
        return this.getExpression('Alert', 'NoProfiles')
      }

      return ''
    },
    headers () {
      return [{
          text: this.getExpression('Label', 'Name'),
          value: 'name',
          align: 'left',
          width: '20%'
        },
        {
          text: this.getExpression('Label', 'Description'),
          value: 'description',
          align: 'left',
          width: '35%'
        },
        {
          text: this.getExpression('Label', 'Media'),
          value: 'items',
          align: 'center',
          width: '15%',
          sortable: false
        },
        {
          text: this.getExpression('Label', 'Active'),
          value: 'isActive',
          align: 'center',
          width: '5%'
        },
        {
          text: this.getExpression('Label', 'Added'),
          value: 'createDate',
          align: 'left',
          width: '20%'
        },
        {
          value: 'action',
          sortable: false,
          width: '5%'
        }
      ]
    },
    dictionary () {
      return {
        custom: {
          name: {
            required: () => this.getExpression('Validation', 'NameRequired'),
            max: this.getExpression('Validation', 'NameMaxChars').replace('{0)', '50')
          }
        }
      }
    },
    toolbarColor () {
      return this.appColor_Resident
    },
    toolbarColorDark () {
      return this.appColor_ResidentDark
    },
    editToolbarColorDark () {
      if (this.isAdmin) return this.appColor_Medium
      return this.appColor_ResidentDark
    },
    isFromDashboard () {
      return this.isFromKioskDashboard || this.isFromResidentDashboard
    },
    isFromResidentDashboard () {
      return  this.previousRoute.indexOf('ResidentDashboard') >= 0
        || (this.previousRoute.indexOf('Home') >= 0 && this.isResidentManager)
    },
    isFromKioskDashboard () {
      return this.previousRoute.indexOf('KioskDashboard') >= 0 
    },
    filteredResidents () {
      if (this.isResidentManager) {
        return this.residents
      }

      const kioskId = this.kioskId
      return this.residents.filter(r => {
        return r.kioskId === kioskId
      })
    },
    residentName () {
      if (this.isNullOrUndefined(this.activeResidentItem)) return null
      return this.activeResidentItem.name
    },
    residentKioskName () {
      if (this.isNullOrUndefined(this.activeResidentKioskItem)) return null
      return this.activeResidentKioskItem.serialNumber
    },
    kioskName () {
      if (this.isNullOrUndefined(this.activeKioskItem)) return null
      return this.activeKioskItem.name
    },
    subscriptionLevel () {
      if (this.isNullOrUndefined(this.activeKioskItem)) return null
      return this.activeKioskItem.subscriptionLevel
    },
    activeProfileId () {
      var profiles = this.profiles.filter(value => {
        return value.isActive
      })
      if (profiles.length > 0) {
        return profiles[0].id
      }
      return -1
    },
    defaultContainer () {
      if (this.residentId !== null) {
        return `${this.residentPrefix}${this.residentId}`
      }
      return this.publicContainer
    },
    disableAdd () {
      return this.residentId === null
    },
    showKiosk () {
      return this.isAdmin && !this.isFromDashboard
    },
    disableKiosk () {
      return this.kiosks.length === 0 || this.isLoading
    },
    showResident () {
      return this.isAdmin && !this.isFromDashboard
    },
    disableResident () {
      return this.filteredResidents.length <= 0 || this.isLoading
    },
    disableSearch () {
      const residentId = this.residentId ?? 0
      return this.isLoading || residentId === 0
    },
    titleClass () {
      return this.isSmallDevice
        ? 'body-2'
        : ''
    },
    searchColWidth () {
      return this.showResident
        ? !this.showKiosk ? '8' : '4'
        : !this.showKiosk ? '12' : '8'
    } 
  },
  methods: {
    loadData () {
      if (!this.isValidSession) return
      const self = this
      self.isLoading = true
      
      self.loadMediaPathTypes().then(() => {
        
        // dashboard mode
        if (self.isFromDashboard) {
          self.residentId = self.activeResidentItem.id

        // admin mode
        } else {
          self.isLoading = true
          appService.getKioskComboboxOptions(self.kioskTypeId_Abby).then(data => {
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              self.kiosks = data.options
              self.kioskId = null
            }
            self.isLoading = false
          })
        }
      })
    },

    loadMediaPathTypes () {
      const self = this
      return new Promise(resolve => {
        const isSystem = true
        const isSharable = true

        appService.getAbbyMediaPathTypes(isSystem, isSharable, self.profile.languageId).then(data => {
          if (data.errorSeverity > 0) {
            self.handleError(data)
          } else {
            self.mediaPathTypes = data.mediaPathTypes
            const excludeTypes = [self.pathTypeId_Ambient, self.pathTypeId_AudioRadioShows]

            self.mediaPathTypes = data.mediaPathTypes
              .filter(pt => !excludeTypes.includes(pt.id))
            
            resolve()
          }
        })
      })
    },

    loadProfiles () {
      const self = this
      self.isLoading = true
      appService.getAbbyResidentProfiles(self.residentId, self.locale).then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.profiles = data.profiles
        }
        self.isLoading = false
      })
    },

    getKioskResidents () {
      const self = this

      return new Promise((resolve) => {
        appService.getKioskResidents(self.kioskId).then(data => {
          if (data.errorSeverity !== null) {
            self.handleError(data)
          } else {
            resolve(data)
          }
        })
      })
    },

    edit (item) {
      this.editedIndex = this.profiles.findIndex(p => p.id === item.id)
      this.editedProfile = Object.assign({}, item)

      this.editedProfile.isDisableActivate = this.editedProfile.id === this.activeProfileId
      this.editDialog = true
    },

    remove (item) {
      const self = this
      const index = self.profiles.findIndex(p => p.id === item.id)
      const name = self.profiles[index].name

      self.$confirm.show({
        title: self.getExpression('FormTitle', 'DeletePlaylist'),
        text: `<b>${name}</b>`,
        onConfirm: () => {
          self.isDeleting = true
          
          const updateByUserId = self.profile.id.replace('auth0|', '')
          appService.deleteAbbyProfile(item.id, true, updateByUserId).then(data => {
            self.isDeleting = false
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              self.profiles.splice(index, 1)
            }
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    save () {
      const self = this
      self.$validator.validateAll().then(valid => {
        if (valid) {
          const isEditMode = self.editedIndex > -1
          self.isSaving = true
          if (isEditMode) {
            var editedProfile = self.profiles[self.editedIndex]
            Object.assign(editedProfile, self.editedProfile)
            const profileModel = self.getModel(editedProfile, true)
            appService.updateAbbyProfile(editedProfile.id, profileModel, true).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.closeDialog()
                if (editedProfile.isActive) {
                  self.setActive(editedProfile.id)
                }
              }
              self.isSaving = false
            })
          } else {
            appService.addAbbyProfile(self.getModel(self.editedProfile, false), true).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.editedProfile.id = data.profile.id
                self.editedProfile.residentId = data.profile.residentId
                self.editedProfile.isActive = data.profile.isActive
                self.editedProfile.createDate = data.profile.createDate
                self.profiles.unshift(self.editedProfile)
                self.profiles[0].profileDetails = []
              }
              self.isSaving = false
              self.closeDialog()
            })
          }
        }
      })
    },

    getModel (p, isEditMode) {
      if (isEditMode) {
        return {
          residentId: this.residentId,
          name: p.name,
          description: p.description,
          isActive: p.isActive,
          updateByUserId: this.profile.id,
          createDate: p.createDate
        }
      } else {
        return {
          residentId: this.residentId,
          name: p.name,
          description: p.description,
          updateByUserId: this.profile.id
        }
      }
    },

    closeDialog () {
      const self = this
      self.editDialog = false
      setTimeout(() => {
        self.resetEditedProfile()
      }, 300)
    },

    resetEditedProfile () {
      this.editedProfile = Object.assign({}, this.defaultProfile)
      this.editedIndex = -1
      this.$validator.reset()
    },

    editMedia (item) {
      const self = this
      self.editedIndex = this.profiles.findIndex(p => p.id === item.id)
      self.editedProfile = Object.assign({}, item)

      self.cloneProfileDetails()
      self.detailsDialog = true
    },

    cloneProfileDetails () {
      const self = this

      self.profileDetails = []
      const profiles = self.profiles.filter(value => {
        return value.id === self.editedProfile.id
      })

      if (profiles.length > 0) {
        const profile = profiles[0]
        profile.profileDetails.forEach(pd => {
          const model = self.getDetailModel(pd)
          self.profileDetails.push(model)
        })
      }
    },

    detailDone ({ profileDetails }) {
      const self = this

      const p = self.profiles.filter(value => {
        return value.id === self.editedProfile.id
      })[0]

      p.profileDetails = []
      profileDetails.forEach(pd => {
        const model = self.getDetailModel(pd)
        p.profileDetails.push(model)
      })
      self.detailsDialog = false
      self.resetEditedProfile()
    },

    getDetailModel (pd) {
      return {
        id: pd.id,
        profileId: pd.profileId,
        mediaFileId: pd.mediaFileId,
        addDate: pd.addDate,
        containerId: pd.containerId,
        container: pd.container,
        containerOwner: pd.containerOwner,
        isLinked: pd.isLinked,
        filename: pd.filename,
        title: pd.title,
        mediaPathTypeId: pd.mediaPathTypeId,
        mediaPath: pd.mediaPath,
        category: pd.category,
        responseTypeId: pd.responseTypeId,
        createDate: pd.createDate,
        contentType: pd.contentType,
        size: pd.size,
        duration: pd.duration,
        thumbnail: pd.thumbnail,
        isProcessing: pd.isProcessing
      }
    },

    detailCancel () {
      this.detailsDialog = false
      this.resetEditedProfile()
    },

    setActive (id) {
      this.profiles.forEach(p => {
        p.isActive = p.id === id
      })
    },

    customFilter (value, search, item) {
      return value != null &&
        search != null &&
        item.name.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
        item.description.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? this.getExpression('FormTitle', 'ValidationFailed')
          : this.getExpression('FormTitle', 'InternalError'),
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  },
  watch: {
    kioskId () {
      const self = this
      self.playlists = []
      self.residentId = null

      if (self.kioskId > 0 && !self.isFromDashboard) {
        self.getKioskResidents().then(data => {  
          self.residents = data.residents
        })
      }
    },

    residentId () {
      if (this.residentId > 0) {
        this.loadProfiles()
      } else this.profiles = []
    },

    editDialog (val) {
      if (val) {
        const self = this
        setTimeout(() => {
          // set focus to name input in the edit dialog
          self.$refs.name.focus()
        }, 300)
      }
    },

    isValidSession (val) {
      if (val === null) return
      this.loadData()
    }
  }
}
</script>
